<template>
    <div id="information-asset-widget-component">
        <widget-wrapper-component :title="$t('information_asset_widget_component.general_view')" id-widget="information-asset-widget">
            <template v-slot:options>
                <menu-actions-component
                    :actions="[
                        {
                            title: $t('global_equipment.edit_equipment'),
                            icon: 'mdi-pencil',
                            emit: 'openEditAssetDialog',
                            has_access: userHasAccess(asset_module.designation, role_edit),
                        },
                    ]"
                    v-on:openEditAssetDialog="openEditAssetDialog"
                />
            </template>

            <template v-slot:default>
                <div id="information-asset-widget-container" class="information-asset-widget-container">
                    <template v-for="(n, index) in total_models">
                        <div :key="n" class="d-flex flex-column position-relative">
                            <template v-if="configuration[index]['is_required']">
                                <div class="asset-info-top d-flex flex-wrap align-center justify-center">
                                    <div class="d-flex justify-center">
                                        <display-asset-content-level-component
                                            :asset="asset"
                                            :asset_module="asset_module"
                                            :index_model="index"
                                            :scale="0.7"
                                        />
                                    </div>

                                    <div class="d-flex flex-column align-center justify-center text-center">
                                        <div
                                            v-if="getConfigurationDatatableAssetVisionData(asset, index, 'percent')"
                                            class="font-weight-bold font-20"
                                            :style="{ color: liquidColor(index) }"
                                        >
                                            {{ getConfigurationDatatableAssetVisionData(asset, index, 'percent') }} %
                                        </div>

                                        <div class="d-flex gap-1 align-baseline">
                                            <span
                                                v-if="getConfigurationDatatableAssetVisionData(asset, index, 'value')"
                                                class="font-weight-bold font-20 font-color-normal"
                                            >
                                                {{ getConfigurationDatatableAssetVisionData(asset, index, 'value') }}
                                                {{ getConfigurationDatatableAssetVisionData(asset, index, 'unit') }}
                                            </span>

                                            <template
                                                v-if="
                                                    getConfigurationDatatableAssetVisionData(asset, index, 'value') &&
                                                    getConfigurationDatatableAssetVisionData(asset, index, 'max')
                                                "
                                            >
                                                <span class="font-20 font-color-light">/</span>
                                            </template>

                                            <span
                                                v-if="getConfigurationDatatableAssetVisionData(asset, index, 'max')"
                                                class="font-20 font-color-light"
                                            >
                                                {{ getConfigurationDatatableAssetVisionData(asset, index, 'max') }}
                                                {{ getConfigurationDatatableAssetVisionData(asset, index, 'unit') }}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div class="asset-info-bottom" :class="{ 'asset-info-bottom--centered': !hasUsefulHollow }">
                                    <div class="text-center">
                                        <p class="mb-1 font-12 font-color-medium">
                                            {{ $t('information_asset_widget_component.content') }}
                                        </p>
                                        <p class="mb-0 font-weight-bold font-16 font-color-normal text-uppercase">
                                            {{
                                                $store.getters['asset_module/liquid_by_id_module_and_id_asset_and_model'](
                                                    asset_module.id_module,
                                                    asset.id_asset,
                                                    index
                                                )?.name
                                            }}
                                        </p>
                                    </div>

                                    <template v-if="hasUsefulHollow">
                                        <v-divider vertical />

                                        <div class="text-center">
                                            <p class="mb-1 font-12 font-color-medium">
                                                {{ $t('useful_hollow') }}
                                            </p>
                                            <p class="mb-0 font-weight-bold font-16 font-color-normal">
                                                {{ displayToHumanNumberWithSpace(asset.useful_hollow) }} {{ getConfigurationDatatableAssetVisionData(asset, index, 'unit') }}
                                            </p>
                                        </div>
                                    </template>
                                </div>
                            </template>

                            <template v-else>
                                <v-icon class="font-color-light"> mdi-cancel</v-icon>
                            </template>
                        </div>
                    </template>
                </div>

                <template v-if="asset.devices && asset.devices.length">
                    <div class="d-flex flex-column gap-2 pt-4">
                        <div
                            v-for="device in asset.devices"
                            :key="device.id_device"
                            class="display-flex-align-center gap-3 flex-wrap"
                        >
                            <div class="display-flex-align-center gap-2">
                                <img
                                    v-if="!isErrorOnDeviceReferentielImage"
                                    :src="device.path_image_device_referentiel"
                                    height="28"
                                    v-on:error="isErrorOnDeviceReferentielImage = true"
                                    alt="Device image"
                                />
                                <v-icon v-else small> mdi-access-point-network </v-icon>
                                <span class="font-14 font-color-default text-overflow-ellipsis">
                                    {{ device.device_number }}
                                </span>
                            </div>

                            <ChipsLastStatement :date="device.date_last_emission" :fallback_text="$t('error_display.no_emission')" />
                            <DisplayDeviceBatteryLevelComponent :battery="device.battery" />
                        </div>
                    </div>
                </template>

                <template v-if="hasAutomaticOrder">
                    <v-alert class="mt-3 mb-0" color="success" dense icon="mdi-refresh" text>
                        {{ $t('oleo_asset_card_widget_component.automatic_order') }}
                    </v-alert>
                </template>
            </template>
        </widget-wrapper-component>

        <EditAssetFormDialogComponent ref="editAssetDialog" />
    </div>
</template>

<script>
import WidgetWrapperComponent from '@/components/global/wrapper/WidgetWrapperComponent.vue'
import DisplayAssetContentLevelComponent from '@/components/equipment/generic/containment/DisplayAssetContentLevelComponent.vue'
import useAsset from '@/mixin/useAsset'
import MenuActionsComponent from '@/components/global/containment/MenuActionsComponent.vue'
import EditAssetFormDialogComponent from '@/components/equipment/generic/form/EditAssetFormDialogComponent.vue'
import ChipsLastStatement from '@/components/global/containment/ChipsLastStatement.vue'
import DisplayDeviceBatteryLevelComponent from '@/components/global/DisplayDeviceBatteryLevelComponent.vue'
import AssetRepository from '@/repositories/AssetRepository'

export default {
    name: 'InformationAssetWidgetComponent',
    components: {
        EditAssetFormDialogComponent,
        MenuActionsComponent,
        DisplayAssetContentLevelComponent,
        WidgetWrapperComponent,
        ChipsLastStatement,
        DisplayDeviceBatteryLevelComponent,
    },
    mixins: [useAsset],
    props: {
        asset: {
            type: Object,
            required: true,
        },
        asset_module: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            isErrorOnDeviceReferentielImage: false,
            tags: [],
        }
    },
    created() {
        this.getAssetTags()
    },
    computed: {
        hasAutomaticOrder() {
            return !!this.tags.find((tag) => tag.label === 'Commande automatique')
        },
        total_models() {
            return this.asset_module.total_models || 0
        },
        configuration() {
            return this.asset.configuration
        },
        hasUsefulHollow() {
            return this.asset.useful_hollow !== null && this.asset.useful_hollow !== undefined
        },
    },
    methods: {
        liquidColor(index) {
            return this.$store.getters['asset_module/liquid_by_id_module_and_id_asset_and_model'](
                this.asset_module.id_module,
                this.asset.id_asset,
                index
            )?.color
        },
        openEditAssetDialog() {
            this.$refs.editAssetDialog.openEditAssetDialog(this.asset)
        },
        async getAssetTags() {
            return AssetRepository.getAssetTags(this.asset.id_asset)
                .then((success) => {
                    this.tags = success.data.data
                })
                .catch((error) => {
                    this.manageError(error)
                })
        },
    },
}
</script>

<style scoped>
.information-asset-widget-container {
    display: grid;
    align-items: stretch;
    column-gap: 8px;
    row-gap: 12px;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
}

.asset-info-top {
    column-gap: 48px;
}

.asset-info-bottom {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: stretch;
    column-gap: 16px;
}

.asset-info-bottom--centered {
    grid-template-columns: 1fr;
}

.asset-info-bottom > .v-divider {
    margin: 4px 0;
}
</style>
